import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import api from '../utils/api';

export const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [transaction, setTransaction] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/browse');
      return;
    }
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    const checkStatus = async (attempt) => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        setTransaction(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.stripe_status === 'expired') {
          setStatus('error');
          return;
        }

        // Continue polling
        if (attempt < maxAttempts) {
          setAttempts(attempt + 1);
          setTimeout(() => checkStatus(attempt + 1), pollInterval);
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkStatus(0);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-20">
        <div className="max-w-md mx-auto text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl font-bold font-heading mb-4" data-testid="payment-checking">
                Processing Payment...
              </h1>
              <p className="text-muted-foreground mb-6">
                Please wait while we confirm your payment. This may take a few moments.
              </p>
              <p className="text-sm text-muted-foreground font-mono">
                Attempt {attempts + 1} of 5
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto mb-6 text-success" strokeWidth={1.5} />
              <h1 className="text-4xl font-bold font-heading mb-4" data-testid="payment-success">
                Payment Successful!
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Congratulations! You've successfully purchased this item.
              </p>
              {transaction && (
                <div className="bg-secondary/50 border border-border p-6 mb-8 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Amount Paid</span>
                    <span className="text-lg font-bold font-mono">${transaction.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Transaction ID</span>
                    <span className="text-xs font-mono">{transaction.transaction_id}</span>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-3">
                {transaction?.auction_id && (
                  <Button
                    onClick={() => navigate(`/auctions/${transaction.auction_id}`)}
                    className="w-full h-12 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                    data-testid="view-auction-button"
                  >
                    View Auction
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="w-full h-12 rounded-sm font-medium border-2"
                  data-testid="go-to-dashboard-button"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="h-16 w-16 mx-auto mb-6 bg-destructive/10 rounded-full flex items-center justify-center">
                <span className="text-3xl">✕</span>
              </div>
              <h1 className="text-3xl font-bold font-heading mb-4" data-testid="payment-error">
                Payment Processing Error
              </h1>
              <p className="text-muted-foreground mb-8">
                We couldn't confirm your payment status. Please check your email for confirmation or contact support.
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="w-full h-12 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                  data-testid="go-to-dashboard-error-button"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/browse')}
                  variant="outline"
                  className="w-full h-12 rounded-sm font-medium border-2"
                  data-testid="browse-auctions-error-button"
                >
                  Browse Auctions
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
